import React from 'react';
import { Clock, AlertTriangle, Wallet, UserCheck, Bell, RotateCcw, X, Loader2 } from 'lucide-react';
import { Button, cx } from '@/components/ui';
import { typeRole } from '@/styles/typography';
import { wfInsetSurfaceClass, wfAccordionPadStyle } from '@/styles/wireframeForm';

interface ManualDepositWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  amount: number;
  providerName: string;
  isLoading?: boolean;
}

const labelClass = `${typeRole.caption} text-caption-muted uppercase font-semibold tracking-wide`;
const bodyClass = `${typeRole.body} text-ink font-ui`;

const ManualDepositWarningModal: React.FC<ManualDepositWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  amount,
  providerName,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 cosmos-modal-backdrop" aria-hidden />
      <div
        className="cosmos-modal-panel relative w-full overflow-visible"
        style={{
          width: 'var(--wf-mdl-payout-w)',
          maxWidth: 'var(--wf-mdl-viewport-max)',
          maxHeight: 'min(var(--wf-mdl-max-h), calc(100dvh - 2rem))',
          height: 'auto',
          borderRadius: 'var(--wf-mdl-radius)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border-ice">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-[8px] bg-warning/15 border border-warning/35 shrink-0">
              <Clock className="w-5 h-5 text-warning" />
            </div>
            <h2 className={`${typeRole.header1} font-bold text-ink m-0`}>Manual Deposit Notice</h2>
          </div>
          <button
            onClick={onClose}
            className="text-caption-muted hover:text-ink transition-colors shrink-0"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col" style={{ gap: 'var(--wf-mdl-gap)' }}>
          <div className="flex items-start gap-3 p-3 rounded-[8px] bg-warning/10 border border-warning/25">
            <AlertTriangle className="w-5 h-5 text-warning mt-0.5 shrink-0" />
            <p className={`${bodyClass} text-warning leading-relaxed m-0`}>
              This game requires manual processing by our staff. Your deposit will be processed within{' '}
              <strong>5 minutes to 2 hours</strong>.
            </p>
          </div>

          <div className={cx(wfInsetSurfaceClass, 'space-y-3 font-ui')} style={wfAccordionPadStyle}>
            <div className="flex items-start gap-3">
              <Wallet className="w-4 h-4 text-info mt-0.5 shrink-0" />
              <p className={`${bodyClass} m-0`}>Funds reserved from wallet immediately</p>
            </div>
            <div className="flex items-start gap-3">
              <UserCheck className="w-4 h-4 text-money-in mt-0.5 shrink-0" />
              <p className={`${bodyClass} m-0`}>Staff deposits manually into game</p>
            </div>
            <div className="flex items-start gap-3">
              <Bell className="w-4 h-4 text-success mt-0.5 shrink-0" />
              <p className={`${bodyClass} m-0`}>Notification sent when confirmed</p>
            </div>
            <div className="flex items-start gap-3">
              <RotateCcw className="w-4 h-4 text-info mt-0.5 shrink-0" />
              <p className={`${bodyClass} m-0`}>Auto-refund after 2 hours if needed</p>
            </div>
          </div>

          <div className={cx(wfInsetSurfaceClass, 'flex items-center justify-between font-ui')} style={wfAccordionPadStyle}>
            <div>
              <p className={`${labelClass} mb-0.5 m-0`}>Amount</p>
              <p className={`${typeRole.header1} font-bold text-ink m-0`}>${amount.toFixed(2)}</p>
            </div>
            <div className="text-center text-caption-muted">&rarr;</div>
            <div className="text-right">
              <p className={`${labelClass} mb-0.5 m-0`}>Game</p>
              <p className={`${typeRole.header1} font-bold text-ink m-0`}>{providerName}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-5 border-t border-border-ice">
          <Button variant="tertiary" size="md" className="flex-1" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="moneyIn"
            size="md"
            className="flex-1"
            onClick={onConfirm}
            disabled={isLoading}
            icon={isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
          >
            {isLoading ? 'Processing...' : 'Confirm Deposit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManualDepositWarningModal;
